// src/Components/ImportMarkers/CoordinateMatchContent.js

import React from 'react';
import {
  TextField,
  IconButton,
  Box,
  CircularProgress,
  Alert,
  Typography,
  Tooltip,
  FormControl,
  OutlinedInput,
  InputAdornment,
} from '@mui/material';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';

import RichTextEditor from '../../utils/RichTextEditor';
import DialogActionBar from './DialogActionBar';
import { SoundWaveIcon } from '../../assets/icons/CustomIconsAdd3';

export default function CoordinateMatchContent({
  open,
  isTypeDesc,
  activePanelData,
  selectedDescPhotoIdx,
  setSelectedDescPhotoIdx,
  photoDescriptions,
  setPhotoDescriptions,
  rteFontType,
  rteFontSize,
  isBulkScraping,
  waterfallRef,
  bulkWaterfallPhotos,
  isManualMatchActive,
  handleDragStart,
  handleDragOver,
  handleDrop,
  handleRemovePhoto,
  isCoordsActive,
  handleResetView,
  viewConfig,
  mapContainerRef,
  errorMsg,
  localTitle,
  setLocalTitle,
  makeUppercase,
  isBulkGenerating,
  handleConfirm,
  localSearchTarget,
  setLocalSearchTarget,
  handleGetLatLng,
  isSearching,
  lat,
  handleManualLatChange,
  lng,
  handleManualLngChange,
  handleCopyLatLng,
  handlePasteLatLng,
  applyToAll,
  setApplyToAll,
  handleCancelClick,
  handleNavUp,
  handleNavDown,
  handleNavPrev,
  handleNavNext,
  isOkDisabled,
  isOkPressed,
  children,
}) {
  if (!open) return null;

  return (
    <Box
      id='coordinate-match-panel'
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}>
      <Box
        sx={{
          width: '50%',
          flexShrink: 0,
          backgroundColor: isTypeDesc ? '#ffffff' : '#e0e0e0',
          position: 'relative',
          height: '100%',
          borderRight: '1px solid #ccc',
          display: isTypeDesc ? 'flex' : 'block',
          boxSizing: 'border-box',
        }}>
        {isTypeDesc ? (
          <>
            <Box
              sx={{
                width: '20%',
                height: '100%',
                overflowY: 'auto',
                p: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                borderRight: '1px solid #ccc',
                backgroundColor: '#f5f5f5',
              }}>
              {!activePanelData?.matchedFiles ||
              activePanelData.matchedFiles.length === 0 ? (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                  }}>
                  <Typography
                    variant='caption'
                    sx={{ color: 'grey', fontStyle: 'italic' }}>
                    No media available for context
                  </Typography>
                </Box>
              ) : (
                activePanelData.matchedFiles.map((file, idx) => {
                  const src = file.isGooglePhoto
                    ? file.sourceUrl
                    : file.preview ||
                      (file instanceof File ? URL.createObjectURL(file) : '');
                  const isSelected = selectedDescPhotoIdx === idx;
                  const isVideo =
                    file.type?.startsWith('video/') ||
                    (file.name && file.name.match(/\.(mp4|mov|avi|wmv|webm)$/i)) ||
                    file.type === 'video';
                  const isAudio =
                    file.type?.startsWith('audio/') ||
                    (file.name && file.name.match(/\.(mp3|wav|ogg|m4a)$/i)) ||
                    file.type === 'audio';

                  return (
                    <Box
                      key={idx}
                      onClick={() => setSelectedDescPhotoIdx(idx)}
                      sx={{
                        width: '100%',
                        aspectRatio: '1',
                        border: isSelected ? '3px solid #1976d2' : '1px solid #ccc',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        flexShrink: 0,
                        backgroundColor: '#fff',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        '&:hover': {
                          borderColor: isSelected ? '#1976d2' : '#999',
                        },
                      }}>
                      {isVideo ? (
                        <video
                          src={`${src}#t=0.1`}
                          preload='metadata'
                          muted
                          playsInline
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : isAudio ? (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#f0f0f0',
                            p: 1,
                            boxSizing: 'border-box',
                          }}>
                          <SoundWaveIcon sx={{ fontSize: 40, mb: 1 }} />
                          <audio
                            src={src}
                            controls
                            style={{ width: '100%', height: '30px' }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Box>
                      ) : (
                        <img
                          src={src}
                          alt={`Reference ${idx}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      )}
                    </Box>
                  );
                })
              )}
            </Box>

            <Box
              sx={{
                width: '80%',
                height: '100%',
                overflowY: 'auto',
                backgroundColor: '#fff',
                display: 'flex',
                flexDirection: 'column',
              }}>
              <RichTextEditor
                key={`rte-desc-${activePanelData?.id}-${selectedDescPhotoIdx}`}
                initialContent={photoDescriptions[selectedDescPhotoIdx] || ''}
                readOnly={false}
                defaultFontType={rteFontType}
                defaultFontSize={rteFontSize}
                placeholder={
                  <Box sx={{ pointerEvents: 'none', textAlign: 'left' }}>
                    <Typography
                      variant='caption'
                      sx={{ color: 'grey', fontStyle: 'italic', display: 'block' }}>
                      Click OK to copy:
                    </Typography>
                    <Box sx={{ pl: 1.5 }}>
                      <Typography
                        variant='caption'
                        sx={{ color: 'grey', fontStyle: 'italic', display: 'block' }}>
                        • the first two paragraphs of the description to the first photo
                        description,
                      </Typography>
                      <Typography
                        variant='caption'
                        sx={{ color: 'grey', fontStyle: 'italic', display: 'block' }}>
                        • the third paragraph to the second photo description,
                      </Typography>
                      <Typography
                        variant='caption'
                        sx={{ color: 'grey', fontStyle: 'italic', display: 'block' }}>
                        • and so on, assigning one paragraph per photo. Remaining text is
                        discarded.
                      </Typography>
                    </Box>
                  </Box>
                }
                onUpdate={(html) => {
                  const rowIdAtUpdate = activePanelData?.id;
                  setPhotoDescriptions((prev) => ({
                    ...prev,
                    _rowId: rowIdAtUpdate,
                    [selectedDescPhotoIdx]: html,
                  }));
                }}
              />
            </Box>
          </>
        ) : isBulkScraping ? (
          <Box
            ref={waterfallRef}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 25,
              p: '3px',
              overflowY: 'auto',
              backgroundColor: '#f5f5f5',
            }}>
            {bulkWaterfallPhotos.length === 0 && (
              <Typography
                variant='body2'
                sx={{
                  width: '100%',
                  textAlign: 'center',
                  mt: 4,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                }}>
                Initializing automated photo search...
              </Typography>
            )}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
                height: '100%',
                justifyContent: 'center',
                alignContent: 'flex-start',
              }}>
              {bulkWaterfallPhotos.map((photo, idx) => (
                <Box
                  key={idx}
                  sx={{
                    flex: '0 0 auto',
                    width: 'calc(50% - 3px)',
                    maxHeight: '100%',
                    aspectRatio: '1',
                    border: '2px solid #ccc',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: 2,
                    backgroundColor: '#fff',
                    position: 'relative',
                  }}>
                  <img
                    src={photo.url}
                    alt={photo.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '4px',
                      left: '4px',
                      right: '4px',
                      backgroundColor: 'rgba(97, 97, 97, 0.9)',
                      borderRadius: '12px',
                      p: '4px 8px',
                    }}>
                    <Typography
                      sx={{
                        color: '#ffffff',
                        fontSize: '10px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: 'bold',
                      }}>
                      {photo.title}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          isManualMatchActive && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 20,
                p: '3px',
                overflowY: 'auto',
                backgroundColor: '#f5f5f5',
              }}>
              {!activePanelData?.matchedFiles ||
              activePanelData.matchedFiles.length === 0 ? (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <Typography variant='body1' sx={{ color: 'grey', fontStyle: 'italic' }}>
                    Select media to include here
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '6px',
                    height: '100%',
                    justifyContent: 'center',
                    alignContent: 'flex-start',
                  }}>
                  {activePanelData.matchedFiles.map((file, idx) => {
                    const src = file.isGooglePhoto
                      ? file.sourceUrl
                      : file.preview ||
                        (file instanceof File ? URL.createObjectURL(file) : '');
                    const isVideo =
                      file.type?.startsWith('video/') ||
                      (file.name && file.name.match(/\.(mp4|mov|avi|wmv|webm)$/i)) ||
                      file.type === 'video';
                    const isAudio =
                      file.type?.startsWith('audio/') ||
                      (file.name && file.name.match(/\.(mp3|wav|ogg|m4a)$/i)) ||
                      file.type === 'audio';

                    return (
                      <Box
                        key={idx}
                        draggable
                        onDragStart={(e) => handleDragStart(e, idx)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, idx)}
                        sx={{
                          flex: '0 0 auto',
                          width: 'calc(50% - 3px)',
                          maxHeight: '100%',
                          aspectRatio: '1',
                          border: '2px solid #ccc',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          boxShadow: 2,
                          backgroundColor: '#fff',
                          cursor: 'grab',
                          position: 'relative',
                          display: 'flex',
                        }}>
                        {isVideo ? (
                          <video
                            src={`${src}#t=0.1`}
                            preload='metadata'
                            muted
                            playsInline
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : isAudio ? (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: '#f0f0f0',
                              p: 1,
                              boxSizing: 'border-box',
                            }}>
                            <SoundWaveIcon sx={{ fontSize: 40, mb: 1 }} />
                            <audio
                              src={src}
                              controls
                              style={{ width: '100%', height: '30px' }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Box>
                        ) : (
                          <img
                            src={src}
                            alt={`Selected ${idx}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        )}
                        <IconButton
                          size='small'
                          onClick={(e) => handleRemovePhoto(e, idx)}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            width: 24,
                            height: 24,
                            minWidth: 24,
                            minHeight: 24,
                            aspectRatio: '1 / 1',
                            borderRadius: '50%',
                            border: '1px solid #000000',
                            padding: 0,
                            boxSizing: 'border-box',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255,255,255,0.85)',
                            color: '#000000',
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,1)',
                            },
                          }}>
                          <CloseIcon sx={{ fontSize: 16, color: '#000000' }} />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )
        )}

        <Box
          sx={{
            display: isManualMatchActive || isTypeDesc ? 'none' : 'block',
            width: '100%',
            height: '100%',
          }}>
          <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 10 }}>
            <Tooltip title='Reset Map View' placement='right'>
              <span>
                <IconButton
                  size='small'
                  onClick={handleResetView}
                  disabled={!viewConfig}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    boxShadow: 1,
                    color: '#333',
                    '&:hover': { backgroundColor: '#fff' },
                  }}>
                  <RefreshIcon fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <div
            ref={mapContainerRef}
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          />

          {!window.google && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}>
              <Typography color='text.secondary'>Map loading...</Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          width: '50%',
          flexShrink: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          boxSizing: 'border-box',
        }}>
        {isCoordsActive ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              overflowY: 'auto',
              py: 2,
              pl: 3,
              pr: '52px',
            }}>
            {errorMsg && (
              <Alert severity='error' sx={{ mb: 0 }}>
                {errorMsg}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', pt: 1 }}>
              <TextField
                label='Title'
                variant='outlined'
                fullWidth
                size='small'
                value={localTitle}
                onChange={(e) => setLocalTitle(e.target.value)}
                disabled={isBulkGenerating}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiInputBase-root': { height: '26px' },
                  '& .MuiInputBase-input': {
                    py: '0px',
                    px: '8px',
                    textTransform: makeUppercase ? 'uppercase' : 'none',
                  },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', width: '100%', pt: 0, pb: 1 }}>
              <FormControl
                variant='outlined'
                fullWidth
                sx={{ height: '36px', justifyContent: 'center' }}>
                <OutlinedInput
                  id='search-target'
                  placeholder='Search Target'
                  value={localSearchTarget}
                  onChange={(e) => setLocalSearchTarget(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleGetLatLng();
                  }}
                  disabled={isSearching || isBulkGenerating}
                  sx={{
                    borderRadius: '50px',
                    height: '36px',
                    backgroundColor:
                      isSearching || isBulkGenerating ? 'rgba(0,0,0,0.02)' : 'white',
                    fontSize: '0.875rem',
                    transition: 'all 0.3s ease',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0,0,0,0.2)',
                      borderWidth: '1px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor:
                        isSearching || isBulkGenerating
                          ? 'rgba(0,0,0,0.2)'
                          : 'rgba(0,0,0,0.4)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0055ff',
                      borderWidth: '2px',
                    },
                    '& .MuiInputBase-input': {
                      textTransform: makeUppercase ? 'uppercase' : 'none',
                    },
                  }}
                  startAdornment={
                    <InputAdornment position='start' sx={{ pl: 0.5 }}>
                      {isSearching ? (
                        <CircularProgress size={18} thickness={5} />
                      ) : (
                        <IconButton
                          id='coords-search-button'
                          size='small'
                          onClick={handleGetLatLng}
                          disabled={
                            isSearching || isBulkGenerating || !localSearchTarget.trim()
                          }
                          sx={{
                            padding: '2px',
                            transition: 'transform 0.2s',
                            '&:hover': {
                              transform:
                                isSearching || isBulkGenerating ? 'none' : 'scale(1.15)',
                              backgroundColor: 'transparent',
                              color:
                                isSearching || isBulkGenerating ? 'inherit' : '#0055ff',
                            },
                          }}>
                          <SearchIcon fontSize='small' />
                        </IconButton>
                      )}
                    </InputAdornment>
                  }
                  endAdornment={
                    localSearchTarget && (
                      <InputAdornment position='end'>
                        <IconButton
                          onClick={() => setLocalSearchTarget('')}
                          size='small'
                          disabled={isSearching || isBulkGenerating}
                          sx={{
                            p: '2px',
                            '&:hover': {
                              color:
                                isSearching || isBulkGenerating ? 'inherit' : '#ff1744',
                            },
                          }}>
                          <CancelIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                />
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 1, alignItems: 'center' }}>
              <TextField
                label='Latitude'
                variant='outlined'
                fullWidth
                size='small'
                value={lat}
                onChange={handleManualLatChange}
                disabled={isBulkGenerating}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiInputBase-root': { height: '26px' },
                  '& .MuiInputBase-input': { py: '0px', px: '8px' },
                }}
              />
              <TextField
                label='Longitude'
                variant='outlined'
                fullWidth
                size='small'
                value={lng}
                onChange={handleManualLngChange}
                disabled={isBulkGenerating}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiInputBase-root': { height: '26px' },
                  '& .MuiInputBase-input': { py: '0px', px: '8px' },
                }}
              />
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                <IconButton
                  onClick={handleCopyLatLng}
                  title='Copy Lat,Lng'
                  size='small'
                  disabled={isBulkGenerating}>
                  <ContentCopyIcon fontSize='small' />
                </IconButton>
                <IconButton
                  onClick={handlePasteLatLng}
                  title='Paste Lat,Lng'
                  size='small'
                  disabled={isBulkGenerating}>
                  <ContentPasteIcon fontSize='small' />
                </IconButton>
              </Box>
            </Box>

            <DialogActionBar
              applyToAllChecked={applyToAll}
              onApplyToAllChange={(e) => setApplyToAll(e.target.checked)}
              disableApplyToAll={isBulkGenerating}
              onCancel={handleCancelClick}
              disableCancel={isSearching}
              onUp={handleNavUp}
              onDown={handleNavDown}
              onPrev={handleNavPrev}
              onNext={handleNavNext}
              disableNav={false}
              onOk={() => handleConfirm()}
              disableOk={isOkDisabled || isBulkGenerating}
              isOkPressed={isOkPressed}
              isLoadingOk={isBulkGenerating}
            />
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
            }}>
            {children}
          </Box>
        )}
      </Box>
    </Box>
  );
}
